// La démo guidée (D-091) : le club de démonstration se visite seul, sans démonstration faite par
// quelqu'un. À l'arrivée, la visite est PROPOSÉE (choix du rôle, durée), quittable ; un bouton
// « Visite guidée » reste visible ; la fin propose d'inscrire son club (30 jours gratuits), la visite
// d'un autre rôle (bascule de compte de démo, comme les boutons de l'écran de connexion) ou d'explorer.
// Vouvoiement : la démo s'adresse à un prospect.
import { demoMode, demoSignIn, type DemoWho } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { DEMO_ROLES, TOURS, type DemoRole, type Tour } from './tours';
import { startTour, tourRunning } from './engine';
import type { Ctx } from '../ui/context';
import { $$, html, openSheet } from '../ui/dom';

const SIGNUP = 'https://arceau.app/?depuis=demo#inscription';
const WELCOMED = 'arceau.guide.demo-accueil';
const PENDING = 'arceau.guide.ensuite';

/** Les comptes de la démo (scripts/seed-local.mjs) : adresses @arceau.local. */
const WHO: Record<string, DemoWho> = { admin: 'admin', coach: 'coach', sarah: 'sarah', parent: 'parent', partenaire: 'partner', karim: 'karim', ines: 'ines', technique: 'technique' };
export const demoWhoOf = (email: string): DemoWho | null => (email.endsWith('@arceau.local') ? WHO[email.split('@')[0]!] ?? null : null);
/** Une session du club de démonstration (instance de test ou démo publique). Pour les essais du guide
 *  « comme dans un vrai club » (scripts/e2e-tours.mjs, captures) : sessionStorage arceau.guide.mode = reel. */
export const isDemoSession = (email: string) => demoMode && demoWhoOf(email) !== null && !forcedReal();
/** Essai « comme dans un vrai club » demandé sur cet onglet. */
export const forcedReal = () => read('arceau.guide.mode') === 'reel';
/** La session en cours est-elle celle de la démo ? */
export const demoNow = async (): Promise<boolean> => demoMode && isDemoSession((await supabase?.auth.getSession())?.data.session?.user.email ?? '');

const roleOfWho = (who: DemoWho | null): DemoRole => (who === 'coach' || who === 'ines' ? 'coach' : who === 'technique' ? 'technique'
  : who === 'sarah' || who === 'parent' ? 'joueur' : who === 'partner' ? 'partenaire' : 'bureau');
const store = (k: string, v: string | null) => { try { if (v === null) sessionStorage.removeItem(k); else sessionStorage.setItem(k, v); } catch { /* navigation privée */ } };
const read = (k: string) => { try { return sessionStorage.getItem(k); } catch { return null; } };

/** Lance la visite d'un rôle ; si c'est un autre compte de démo, on s'y connecte d'abord. */
export async function demoTour(role: DemoRole, email: string): Promise<void> {
  const r = DEMO_ROLES.find(x => x.role === role)!;
  const me = demoWhoOf(email);
  if (me === r.who || (role === 'joueur' && me === 'parent')) { startTour(`demo-${role}`); return; }
  store(PENDING, `demo-${role}`);
  const res = await demoSignIn(r.who);
  if (!res.ok) store(PENDING, null);
}

/** À chaque page de la démo : la visite qui suit une bascule de compte, l'accueil une fois, le bouton. */
export function demoAfterRender(ctx: Ctx, email: string): void {
  const pending = read(PENDING);
  if (pending && TOURS[pending]) { store(PENDING, null); store(WELCOMED, '1'); startTour(pending); }
  else if (!read(WELCOMED) && !tourRunning()) { store(WELCOMED, '1'); openDemoWelcome(ctx, email); }
  drawFab(ctx, email);
}

/** « Bienvenue dans la démo » : le choix de la visite (maquette a1). */
export function openDemoWelcome(ctx: Ctx, email: string): void {
  let pick: DemoRole = roleOfWho(demoWhoOf(email));
  const label = (r: DemoRole) => `Commencer la visite ${r === 'bureau' ? 'du bureau' : r === 'coach' ? 'du coach' : r === 'technique' ? 'du resp. technique' : r === 'joueur' ? 'de la joueuse' : 'du partenaire'} →`;
  openSheet('Bienvenue dans la démo', html`
    <div class="gd-welcome">
      <p class="lead">Le <b>${ctx.club?.name ?? 'club de démo'}</b> est un club inventé : touchez à tout, rien n’est envoyé à personne. Choisissez une visite guidée, ou explorez librement.</p>
      <div class="gd-roles" role="radiogroup" aria-label="Visites guidées">${DEMO_ROLES.map(r => html`
        <button type="button" class="gd-role ${r.role === pick ? 'on' : ''}" role="radio" aria-checked="${String(r.role === pick)}" data-role="${r.role}">
          <span class="ic" aria-hidden="true">${r.icon}</span><span class="t"><strong>${r.title}</strong><small>${r.sub}</small></span><span class="d">${r.minutes} MIN</span></button>`)}</div>
      <button type="button" class="btn btn-primary gd-wide" id="gd-go">${label(pick)}</button>
      <button type="button" class="btn btn-ghost gd-wide" data-close>Explorer librement</button>
    </div>`, (sheet, close) => {
    const go = sheet.querySelector<HTMLButtonElement>('#gd-go')!;
    for (const b of $$<HTMLButtonElement>(sheet, '[data-role]')) {
      b.addEventListener('click', () => {
        pick = b.dataset.role as DemoRole;
        $$(sheet, '[data-role]').forEach(x => { x.classList.toggle('on', x === b); x.setAttribute('aria-checked', String(x === b)); });
        go.textContent = label(pick);
      });
    }
    go.addEventListener('click', () => { close(); void demoTour(pick, email); });
  });
}

/** Le bouton « Visite guidée », toujours visible dans la démo (caché pendant une visite). */
function drawFab(ctx: Ctx, email: string): void {
  let fab = document.querySelector<HTMLButtonElement>('.gd-fab');
  if (!fab) {
    fab = document.createElement('button');
    fab.type = 'button';
    fab.className = 'gd-fab';
    fab.innerHTML = '<span class="ball" aria-hidden="true"></span>Visite guidée';
    document.body.append(fab);
  }
  fab.onclick = () => openDemoWelcome(ctx, email);
}

/** La carte de fin d'une visite de la démo (maquette a4). */
export function demoEndCard(t: Tour, email: string, close: () => void): HTMLElement {
  const other = DEMO_ROLES.find(r => r.role === t.end!.other)!;
  const n = t.steps.length + 1;
  const card = document.createElement('div');
  card.className = 'gd-end';
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-label', t.end!.title);
  card.innerHTML = html`
    <div class="gd-k"><span>${t.kicker}</span><span>${n} / ${n}</span></div>
    <h4>${t.end!.title}</h4>
    <ul>${t.end!.points.map(p => html`<li>${p}</li>`)}</ul>
    <a class="gd-next" href="${SIGNUP}" target="_top">Inscrire mon club · 30 jours gratuits</a>
    <div class="row"><button type="button" class="gd-ghost" data-other="${other.role}">${other.other}</button><button type="button" class="gd-ghost" data-explore>Explorer</button></div>
    <small>Sans engagement · sans carte bancaire</small>`.s;
  card.querySelector('[data-explore]')?.addEventListener('click', close);
  card.querySelector<HTMLButtonElement>('[data-other]')?.addEventListener('click', (e) => {
    close();
    void demoTour((e.currentTarget as HTMLButtonElement).dataset.other as DemoRole, email);
  });
  return card;
}
